import { useId, useMemo, useState, type ReactNode } from 'react';
import { errorMessage } from '../api/client';
import { readableCategoryName } from '../api/accounts';
import type { DdRequest, RoomDocument } from '../api/types';
import { formatDateOnly } from '../lib/date-only';
import { rankLinkCandidates } from '../lib/link-candidates';
import { Button } from './button';
import { ConfirmDialog } from './confirm-dialog';
import { Dialog } from './dialog';
import { Input } from './input';

/** The action's label, the same on every checklist surface. */
export const LINK_EXISTING_LABEL = 'Link a document already in the room';

/** How many rows the picker renders before "Show more" (a room can hold
 * thousands of documents; the search box is the way to reach the rest). */
export const LINK_PICKER_PAGE = 50;

/** One picker row's second line: where the document is filed. */
function filingPath(doc: RoomDocument): string {
  const path = Array.isArray(doc.categoryPath) ? doc.categoryPath : [];
  return path.length > 0 ? path.map(readableCategoryName).join(' › ') : 'Not filed yet';
}

/**
 * The picker for linking documents ALREADY IN THE DATA ROOM to one checklist
 * item (owner ask 2026-10-15), shared by the admin checklist and the seller's.
 *
 * Lists exactly the documents the caller's own listing returned (`documents`) —
 * it adds no visibility — likely matches first (filed to the item's category, or
 * dated in the period its title names; see {@link rankLinkCandidates}), then the
 * rest. A search box narrows by name; each row shows the name, where it is
 * filed, and its date of data; several can be chosen. "Link" stays disabled
 * until something is chosen, then runs `onLink` with the chosen ids; the dialog
 * closes when it resolves and shows the error inline when it rejects.
 */
export function LinkDocumentDialog({
  item,
  documents,
  onLink,
  onClose,
}: {
  item: Pick<DdRequest, 'title' | 'categoryTagId' | 'categoryPath' | 'linkedDocumentIds'>;
  documents: readonly RoomDocument[];
  /** Links the chosen documents (the caller's mutation). Reject to show an error. */
  onLink: (documentIds: string[]) => Promise<unknown>;
  onClose: () => void;
}) {
  const searchId = useId();
  const rowId = useId();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [limit, setLimit] = useState(LINK_PICKER_PAGE);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { likely, others } = useMemo(
    () => rankLinkCandidates(item, documents, query),
    [item, documents, query],
  );
  // A document linked meanwhile (e.g. the first of several before an error)
  // drops out of the choice.
  const linked = new Set(item.linkedDocumentIds);
  const chosen = [...selected].filter((id) => !linked.has(id));
  const available = documents.some((d) => !linked.has(d.id));

  const shownLikely = likely.slice(0, limit);
  const shownOthers = others.slice(0, Math.max(0, limit - shownLikely.length));
  const hidden = likely.length + others.length - shownLikely.length - shownOthers.length;

  function toggle(id: string, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function link() {
    if (chosen.length === 0 || pending) return;
    setPending(true);
    setError(null);
    try {
      await onLink(chosen);
      onClose();
    } catch (err) {
      setError(errorMessage(err) ?? 'Those documents weren’t linked. Try again.');
      setPending(false);
    }
  }

  const row = (doc: RoomDocument) => {
    const checked = selected.has(doc.id);
    const nameId = `${rowId}-${doc.id}-name`;
    const infoId = `${rowId}-${doc.id}-info`;
    return (
      <li key={doc.id}>
        <label className="flex cursor-pointer items-start gap-3 rounded-md px-2 py-2 hover:bg-surface-2 focus-within:ring-2 focus-within:ring-ring">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0"
            checked={checked}
            aria-labelledby={nameId}
            aria-describedby={infoId}
            disabled={pending}
            onChange={(e) => toggle(doc.id, e.target.checked)}
          />
          <span className="min-w-0">
            <span id={nameId} className="block break-words text-sm font-medium text-ink">
              {doc.name}
            </span>
            <span id={infoId} className="block">
              <span className="block break-words text-xs text-ink-2">{filingPath(doc)}</span>
              <span className="block text-xs text-ink-3">
                {doc.dataDate ? `Data as of ${formatDateOnly(doc.dataDate)}` : 'No date of data'}
              </span>
            </span>
          </span>
        </label>
      </li>
    );
  };

  const group = (heading: string, docs: RoomDocument[]): ReactNode =>
    docs.length > 0 ? (
      <section aria-label={heading} className="space-y-1">
        <h3 className="px-2 text-xs font-semibold uppercase tracking-wide text-ink-3">{heading}</h3>
        <ul className="space-y-0.5">{docs.map(row)}</ul>
      </section>
    ) : null;

  return (
    <Dialog
      title={LINK_EXISTING_LABEL}
      description={<>For “{item.title}”. Choose one or more; each then counts toward this item.</>}
      onClose={onClose}
      busy={pending}
      footer={
        <>
          <span className="text-sm text-ink-2" aria-live="polite">
            {chosen.length === 0
              ? 'Nothing chosen yet'
              : `${chosen.length} document${chosen.length === 1 ? '' : 's'} chosen`}
          </span>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" disabled={pending} onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={pending || chosen.length === 0} onClick={() => void link()}>
              {pending ? 'Linking…' : 'Link'}
            </Button>
          </div>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <label htmlFor={searchId} className="text-sm font-medium">
            Search by name
          </label>
          <Input
            id={searchId}
            type="search"
            value={query}
            placeholder="e.g. balance sheet"
            autoComplete="off"
            onChange={(e) => {
              setQuery(e.target.value);
              setLimit(LINK_PICKER_PAGE);
            }}
          />
        </div>
        {!available ? (
          <p className="text-sm text-muted-foreground">
            There are no other documents in the room to link yet.
          </p>
        ) : likely.length + others.length === 0 ? (
          <p className="text-sm text-muted-foreground" role="status">
            No documents match “{query.trim()}”.
          </p>
        ) : (
          <div className="space-y-4">
            {group('Likely matches', shownLikely)}
            {group(likely.length > 0 ? 'Everything else' : 'Documents in the room', shownOthers)}
            {hidden > 0 ? (
              <p className="flex flex-wrap items-center gap-2 px-2 text-xs text-muted-foreground">
                <span>{hidden} more — search to narrow, or</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setLimit((n) => n + LINK_PICKER_PAGE)}
                >
                  Show {Math.min(LINK_PICKER_PAGE, hidden)} more
                </Button>
              </p>
            ) : null}
          </div>
        )}
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

/**
 * The "Link a document already in the room" action for one checklist item: the
 * trigger plus its {@link LinkDocumentDialog}. `appearance` matches the
 * surrounding row — an outline button, or a text link in a row of text actions.
 */
export function LinkExistingDocumentAction({
  item,
  documents,
  onLink,
  appearance = 'button',
  disabled = false,
}: {
  item: Pick<DdRequest, 'title' | 'categoryTagId' | 'categoryPath' | 'linkedDocumentIds'>;
  documents: readonly RoomDocument[];
  onLink: (documentIds: string[]) => Promise<unknown>;
  appearance?: 'button' | 'link';
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {appearance === 'button' ? (
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          aria-haspopup="dialog"
          onClick={() => setOpen(true)}
        >
          {LINK_EXISTING_LABEL}
        </Button>
      ) : (
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="dialog"
          className="rounded text-left text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:text-muted-foreground disabled:no-underline"
          onClick={() => setOpen(true)}
        >
          {LINK_EXISTING_LABEL}
        </button>
      )}
      {open ? (
        <LinkDocumentDialog
          item={item}
          documents={documents}
          onLink={onLink}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

/**
 * The confirmation before unlinking a document from a checklist item. Unlinking
 * is reversible (link it again), and the document stays in the data room; the
 * item goes back to Missing only if it was its last document.
 */
export function UnlinkDocumentDialog({
  itemTitle,
  documentName,
  pending = false,
  error,
  onConfirm,
  onCancel,
}: {
  itemTitle: string;
  documentName: string;
  pending?: boolean;
  /** Shown under the statement (e.g. the API's error). */
  error?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <ConfirmDialog
      title="Unlink this document?"
      confirmLabel="Unlink"
      pending={pending}
      onCancel={onCancel}
      onConfirm={onConfirm}
    >
      <p>
        <strong>“{documentName}”</strong> will no longer count toward <strong>“{itemTitle}”</strong>.
        It stays in the data room.
      </p>
      <p>If it is the item’s only document, the item goes back to Missing.</p>
      {error}
    </ConfirmDialog>
  );
}
